import React, { useState, useEffect, useRef } from 'react';
import { array, object, func } from 'prop-types';
import styled from 'styled-components';
import { I18n } from 'react-redux-i18n';
import CustomSelect from '../../../reusable/Selects/Select';
import CompactSelect from '../../../reusable/Selects/CompactSelect';

import ReportsSet, { findIfReportIsDisabled } from '../../../Respondents/RequestReportModal/Set';
import Solution from '../../../Respondents/RequestReportModal/Solution';

import { defineDependencies } from '../../../Respondents/RequestReportModal/ReportsList';
import { ReactComponent as Warning } from '../../../../assets/icons/small-warning.svg';

const AssessmentProjectReportsList = ({
  languages,
  language,
  setLanguage,
  reports,
  commonTests,
  norms,
  activeNorms,
  idealProfiles,
  activeIdealProfiles,
  onNormChange,
  onIdealProfileChange,
  onSelectedReportsChange,
  setOptionsStatus,
}) => {
  const [data, setData] = useState(reports.map((item) => ({ ...item, isOpen: item.isChecked })));
  const reportsListRef = useRef(null);
  useEffect(() => {
    setData(reports.map((item) => ({ ...item, isOpen: item.isChecked })));
  }, [reports]);

  useEffect(() => {
    onSelectedReportsChange(data);
  }, [data]);

  const onSetClick = (id) => {
    setData((prev) => prev.map((item) => ({ ...item, isOpen: id === item.reportSetID ? !item.isOpen : item.isOpen })));
    const isLast = data[data.length - 1].reportSetID === id;
    if (isLast && !data[data.length - 1].isOpen)
      setTimeout(() => reportsListRef.current.scrollBy({ top: 200, behavior: 'smooth' }), 350);
  };

  const onSetCheckboxClick = (setId) => {
    setData((prev) => {
      return prev.map((report) =>
        report.reportSetID === setId
          ? {
              ...report,
              isChecked: !report.isChecked,
              reports: report.reports.map((item) => ({
                ...item,
                isChecked: findIfReportIsDisabled(item, commonTests, idealProfiles) ? false : !report.isChecked,
              })),
            }
          : report,
      );
    });
  };

  const onSetReportClick = (setId, reportId) => {
    setData((prev) =>
      prev.map((report) => {
        if (report.reportSetID !== setId) return report;
        const wasChildChecked = report.reports.find((report) => report.reportID === reportId).isChecked;
        const checkedChildren = report.reports.filter((report) => report.isChecked);
        return {
          ...report,
          isChecked: !(report.isChecked && wasChildChecked && checkedChildren.length <= 1),
          reports: report.reports.map((report) =>
            report.reportID === reportId ? { ...report, isChecked: !report.isChecked } : report,
          ),
        };
      }),
    );
  };

  const deps = defineDependencies(data);
  const normsToShow = deps.normsDependencies.map((key) => (norms[key] ? norms[key] : undefined)).filter((item) => item);

  const profilesToShow = deps.profilesDependencies
    .map((key) => (idealProfiles[key] ? idealProfiles[key] : undefined))
    .filter((item) => item);

  useEffect(() => {
    setOptionsStatus((prev) => {
      const newNormsStatus = Boolean(normsToShow.length);
      const newIdealProfilesStatus = Boolean(profilesToShow.length);
      if (prev.norms === newNormsStatus && prev.idealProfiles === newIdealProfilesStatus) return prev;
      return { norms: newNormsStatus, idealProfiles: newIdealProfilesStatus };
    });
  }, [normsToShow.length, profilesToShow.length]);

  const showOptions = Boolean(normsToShow.length || profilesToShow.length);

  useEffect(() => {
    if (!showOptions) {
      onNormChange(null);
      onIdealProfileChange(null);
    }
  }, [showOptions]);

  return (
    <Container>
      <CustomSelect
        placeholder={I18n.t('Language')}
        options={languages}
        selectProps={{
          value: language,
          onChange: setLanguage,
        }}
        allowSearch
      />
      {data.length ? (
        <MainSectionWrapper>
          <ReportsWrapper showOptions={showOptions}>
            <ReportsItemsList ref={reportsListRef}>
              {data.map((item) => {
                return item.isSolutionSet ? (
                  <Solution
                    key={item.reportSetID}
                    solution={item}
                    onSolutionClick={onSetClick}
                    onReportClick={onSetReportClick}
                    onSolutionCheckboxClick={onSetCheckboxClick}
                    compact
                    idealProfiles={idealProfiles}
                  />
                ) : (
                  <ReportsSet
                    key={item.reportSetID}
                    set={item}
                    onReportClick={onSetReportClick}
                    onSetCheckboxClick={onSetCheckboxClick}
                    onSetClick={onSetClick}
                    commonTests={commonTests}
                    compact
                    idealProfiles={idealProfiles}
                  />
                );
              })}
            </ReportsItemsList>
          </ReportsWrapper>
          <OptionsWrapper showOptions={showOptions}>
            {Boolean(normsToShow.length) && (
              <NormsList areProfilesVisible={Boolean(profilesToShow.length)}>
                {normsToShow.map((norm) => {
                  return (
                    <CompactSelect
                      key={norm.name}
                      options={norm.options}
                      label={`${norm.name} ${I18n.t('Norm')}`}
                      selectProps={{ value: activeNorms[norm.name], onChange: (val) => onNormChange(val) }}
                    />
                  );
                })}
              </NormsList>
            )}
            {Boolean(profilesToShow.length) && (
              <IdealProfilesList areNormsVisible={Boolean(normsToShow.length)}>
                {profilesToShow.map((idealProfile) => {
                  return (
                    <CompactSelect
                      key={idealProfile.name}
                      options={idealProfile.options}
                      label={`${idealProfile.name} ${I18n.t('Ideal Profile')}`}
                      selectProps={{
                        value: activeIdealProfiles[idealProfile.name],
                        onChange: (val) => onIdealProfileChange(val),
                      }}
                    />
                  );
                })}
              </IdealProfilesList>
            )}
          </OptionsWrapper>
        </MainSectionWrapper>
      ) : (
        <EmptyContainer>
          <Warning />
          <Subtitle>{I18n.t('There are no reports available for selected test and language')}</Subtitle>
          <Text>{I18n.t('Please make sure you selected correct language or contact your administrator for help')}</Text>
        </EmptyContainer>
      )}
    </Container>
  );
};

AssessmentProjectReportsList.propTypes = {
  languages: array.isRequired,
  language: object,
  setLanguage: func.isRequired,
  reports: array.isRequired,
  commonTests: array.isRequired,
  norms: object.isRequired,
  idealProfiles: object.isRequired,
  onNormChange: func.isRequired,
  onIdealProfileChange: func.isRequired,
  onSelectedReportsChange: func.isRequired,
  setOptionsStatus: func.isRequired,
  activeNorms: object,
  activeIdealProfiles: object,
};

AssessmentProjectReportsList.defaultProps = {
  language: {
    value: 'EN',
    code: 'EN',
    label: 'English',
  },
  activeNorms: {},
  activeIdealProfiles: {},
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding-top: 1rem;
`;

const MainSectionWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  justify-content: space-between;
`;

const ReportsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: ${(props) => (props.showOptions ? '60%' : '100%')};
  transition: width 0.3s;
  max-height: 27rem;
  height: calc(100vh - 30rem);
`;

const ReportsItemsList = styled.div`
  display: block;
  flex-direction: column;
  overflow: auto;
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none; /* Remove scrollbar space */
  }
`;

const OptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: ${(props) => (props.showOptions ? '38%' : '0')};
  opacity: ${(props) => (props.showOptions ? 1 : 0)};
  transition: width, opacity 0.3s;
  max-height: 21rem;
`;

const NormsList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 15px;
  border: 1px solid #cccccc;
  border-radius: 7px;
  overflow: auto;
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none; /* Remove scrollbar space */
  }
  height: ${(props) => {
    const baseHeight = 340;
    return props.areProfilesVisible ? `${baseHeight - 100}px` : 'auto';
  }};
  margin-bottom: ${(props) => (props.areProfilesVisible ? '5px' : 0)};
  flex-grow: 3;
`;

const IdealProfilesList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 15px;
  border: 1px solid #cccccc;
  border-radius: 7px;
  overflow: auto;
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none; /* Remove scrollbar space */
  }
  height: ${(props) => (props.areNormsVisible ? `100px` : 'auto')};
  flex-grow: 1;
`;

const EmptyContainer = styled.div`
  height: 70%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Subtitle = styled.p`
  font-size: ${(props) => props.theme.fontSizes.normal};
  color: ${(props) => props.theme.colors.halfBlack};
  margin-bottom: 0.5rem;
  margin-top: 2rem;
`;

const Text = styled.p`
  font-size: ${(props) => props.theme.fontSizes.small};
  color: rgba(0, 0, 0, 0.4);
`;

export default AssessmentProjectReportsList;
